import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { baseURL, getComments, getCommentsReply } from "../redux/Action";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import Replies from "./Replies";
import ReplyInput from "./ReplyInput";
import CommentCard from "./CommentCard";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Comments = ({ id, setComments }) => {
  const {
    commentLoading,
    commentError,
    comments,
    user,
    isAuth,
    replies,
    token,
    replyError,
    replyLoading,
  } = useSelector((state) => state);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [reply, setReply] = useState(false);
  const [replyInd, setReplyInd] = useState(-1);
  const [comment, setComment] = useState("");
  const [showReplies, setShowReplies] = useState(false);

  useEffect(() => {
    getComments(dispatch, id);
  }, [dispatch, id]);

  useEffect(() => {
    setComments(comments);
    // eslint-disable-next-line
  }, [comments]);

  const deleteComment = (_id, el) => {
    const toastId = toast.loading("Executing delete...", { theme: "dark" });
    axios
      .delete(`${baseURL}/comments/${_id}`, {
        withCredentials: true,
        headers: {
          Authorization: token,
        },
      })
      .then((res) => {
        toast.update(toastId, {
          render: "Purged Successfully",
          type: "success",
          isLoading: false,
          theme: "dark",
          autoClose: 2000,
          closeOnClick: true,
        });
        getComments(dispatch, id);
        if (el) getCommentsReply(dispatch, el);
      })
      .catch((err) => {
        toast.update(toastId, {
          render: "Error: Operation failed.",
          type: "error",
          isLoading: false,
          autoClose: 2000,
          closeOnClick: true,
        });
      });
  };

  const handleReply = (i) => {
    if (!isAuth) {
      navigate("/login");
      return;
    }
    if (replyInd === i) {
      setReply(!reply);
    } else {
      setReplyInd(i);
      setReply(true);
    }
  };

  const handleSubmit = (el) => {
    const payload = {
      user_id: user.user._id,
      blog_id: id,
      message: comment,
      rating: 0,
      parentId: el._id,
    };
    const toastId = toast.loading("Transmitting...", { theme: "dark" });
    axios
      .post(`${baseURL}/comments`, payload, {
        withCredentials: true,
        headers: {
          Authorization: token,
        },
      })
      .then((res) => {
        toast.update(toastId, {
          render: "Transmitted",
          type: "success",
          isLoading: false,
          theme: "dark",
          autoClose: 2000,
          closeOnClick: true,
        });
        getCommentsReply(dispatch, el._id);
        setShowReplies(true);
        setComment("");
        setReply(false);
      })
      .catch((err) => {
        toast.update(toastId, {
          render: "Transmission Failed",
          type: "error",
          isLoading: false,
          autoClose: 2000,
          closeOnClick: true,
        });
      });
  };

  const getRepliesToShow = (el, i) => {
    if (replyInd === i) {
      setShowReplies(!showReplies);
      if (!showReplies) getCommentsReply(dispatch, el._id);
    } else {
      setReplyInd(i);
      setShowReplies(true);
      getCommentsReply(dispatch, el._id);
    }
  };

  if (commentLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  if (commentError) {
    return (
      <Stack sx={{ maxWidth: "100%", mt: 4 }} spacing={2}>
        <Alert
          sx={{
            borderRadius: 0,
            border: "1px solid currentColor",
            backgroundColor: "transparent",
          }}
          severity="error"
        >
          <span className="font-mono uppercase font-bold text-xs tracking-widest text-black dark:text-white">
            SYSTEM CRITICAL: DATA CORRUPTION
          </span>
        </Alert>
      </Stack>
    );
  }

  return (
    <div className="space-y-6">
      {comments.length === 0 ? (
        <div className="text-center py-10 bg-white dark:bg-black border border-black dark:border-white/30 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[4px_4px_0px_0px_rgba(255,255,255,0.5)]">
          <p className="font-mono text-xs uppercase font-bold tracking-widest text-gray-500 dark:text-gray-400">
            [ DATABANK EMPTY: NO APPENDICES FOUND ]
          </p>
        </div>
      ) : (
        comments.map((c, i) => (
          <div
            key={c._id || i}
            className="relative border border-black dark:border-white/30 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[4px_4px_0px_0px_rgba(255,255,255,0.5)] bg-white dark:bg-black"
          >
            <CommentCard
              handleReply={handleReply}
              deleteComment={deleteComment}
              el={c}
              i={i}
              id={id}
              user={user}
            />

            {reply && i === replyInd && (
              <ReplyInput
                el={c}
                handleSubmit={handleSubmit}
                comment={comment}
                reply={reply}
                setComment={setComment}
                setReply={setReply}
                user={user}
              />
            )}

            {/* View Replies Toggle */}
            <div className="p-4 border-t border-black dark:border-white/30 bg-[#f4f4f4] dark:bg-[#111] flex justify-end">
              <button
                className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-black dark:text-white hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black border border-transparent hover:border-black dark:hover:border-white px-4 py-2 transition-colors"
                onClick={() => getRepliesToShow(c, i)}
              >
                {showReplies && i === replyInd ? (
                  <>
                    <FiChevronUp className="text-sm" /> CLOSE LOGS
                  </>
                ) : (
                  <>
                    <FiChevronDown className="text-sm" /> EXTRACT LOGS
                  </>
                )}
              </button>
            </div>

            {/* Replies Section */}
            {replyLoading && i === replyInd ? (
              <div className="p-4 bg-gray-100 dark:bg-[#0a0a0a] border-t border-black dark:border-white/30 flex justify-center">
                <Box sx={{ display: "flex", py: 2 }}>
                  <CircularProgress size={24} color="inherit" />
                </Box>
              </div>
            ) : replyError && i === replyInd ? (
              <div className="p-4 bg-gray-100 dark:bg-[#0a0a0a] border-t border-black dark:border-white/30">
                <Alert sx={{ borderRadius: 0 }} severity="error">
                  <span className="font-mono uppercase font-bold text-xs">
                    EXTRACTION FAILED
                  </span>
                </Alert>
              </div>
            ) : (
              showReplies &&
              i === replyInd && (
                <div className="bg-gray-100 dark:bg-[#0a0a0a] border-t border-black dark:border-white/30 p-4">
                  {replies.length === 0 ? (
                    <p className="text-[10px] font-mono font-bold tracking-widest uppercase text-gray-500 py-4 text-center">
                      NO RELATED LOGS FOUND.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {replies
                        .sort(
                          (a, b) =>
                            new Date(a.createdAt) - new Date(b.createdAt),
                        )
                        .map((el, idx) => (
                          <Replies
                            key={el._id || idx}
                            i={idx}
                            parent={c}
                            el={el}
                            deleteComment={deleteComment}
                            user={user?.user}
                          />
                        ))}
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default Comments;
